/**
 * 条件分支节点组件
 *
 * 基于 LLM 判断的条件分支
 * 左侧输入连接点 + 多个右侧输出连接点（每个分支一个）
 */

import React, { memo } from 'react';
import type { NodeProps } from '@xyflow/react';
import { Handle, Position } from '@xyflow/react';
import type { ConditionNodeData } from '../../../types/workflow';
import { InputHandle } from './CommonHandle';
import { useNodeExecutionStatus } from '../../../hooks/useNodeExecutionStatus';
import {
  cn,
  getNodeContainerClasses,
  getNodeHeaderClasses,
} from './nodeStyles';

const ConditionNodeComponent: React.FC<NodeProps> = ({
  id,
  data,
  selected,
}) => {
  // 类型断言
  const nodeData = data as unknown as ConditionNodeData;

  // 使用统一的执行状态 Hook
  const {
    executionStatus,
    isRunning,
    isCompleted,
    isFailed,
  } = useNodeExecutionStatus(id);

  // 分支数量
  const branchCount = nodeData.branches?.length || 0;

  return (
    <div
      className={getNodeContainerClasses('condition', executionStatus, selected, isRunning)}
      data-node-id={id}
      data-node-type="condition"
      data-execution-status={executionStatus}
    >
      {/* 节点头部 */}
      <div className={getNodeHeaderClasses('condition')}>
        {/* 分支图标 */}
        <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-medium text-amber-600 flex-1 truncate">
          {nodeData.label || '条件分支'}
        </span>
        {/* 分支数量徽章 */}
        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
          {branchCount} 分支
        </span>
      </div>

      {/* 节点内容 - 条件列表 */}
      <div className="px-3 py-2 space-y-1.5">
        {nodeData.branches && nodeData.branches.length > 0 ? (
          nodeData.branches.map((branch, index) => (
            <div
              key={branch.id}
              className={cn(
                'flex items-center gap-2 text-xs py-1 px-2 rounded',
                branch.id === nodeData.default_branch_id
                  ? 'bg-gray-100 text-gray-500'
                  : 'bg-amber-50 text-amber-700',
              )}
            >
              {/* 分支序号 */}
              <span className="w-4 h-4 flex items-center justify-center rounded-full bg-white text-amber-600 text-[10px] font-medium border border-amber-200">
                {index + 1}
              </span>
              <span className="flex-1 truncate">{branch.label}</span>
              {branch.id === nodeData.default_branch_id && (
                <span className="text-[10px] text-gray-400">默认</span>
              )}
            </div>
          ))
        ) : (
          <div className="text-xs text-gray-400 italic py-1">
            请添加条件分支
          </div>
        )}
      </div>

      {/* 描述 */}
      {nodeData.description && (
        <div className="px-3 pb-2 text-xs text-gray-400">
          {nodeData.description}
        </div>
      )}

      {/* 输入连接点（左侧） */}
      <InputHandle
        isHighlighted={isRunning}
        isCompleted={isCompleted}
        isFailed={isFailed}
      />

      {/* 多个输出连接点（右侧，每个分支一个） */}
      {nodeData.branches && nodeData.branches.map((branch, index) => {
        // 计算垂直位置：从上到下均匀分布
        const headerHeight = 40;
        const contentStartY = headerHeight + 8;
        const branchHeight = 28;
        const yPosition = contentStartY + (index * branchHeight) + 14;

        return (
          <Handle
            key={branch.id}
            type="source"
            position={Position.Right}
            id={branch.id}
            className={cn(
              'w-3 h-3 rounded-full border-2 transition-all duration-150 cursor-pointer',
              branch.id === nodeData.default_branch_id
                ? 'bg-gray-100 border-gray-300'
                : 'bg-amber-50 border-amber-300',
              'hover:border-amber-500 hover:bg-amber-100',
              isRunning && 'border-amber-500 shadow-lg shadow-amber-200 animate-pulse',
              isCompleted && 'bg-success-500 border-success-500',
              isFailed && 'bg-error-500 border-error-500',
            )}
            style={{
              top: yPosition,
            }}
          />
        );
      })}
    </div>
  );
};

ConditionNodeComponent.displayName = 'ConditionNode';

export const ConditionNode = memo(ConditionNodeComponent);
export default ConditionNode;
